import React, { useMemo } from 'react';
import { formatMoney } from '../../../../utils/constants/costs';
import { mapBackendCostsToUi } from '../../../../utils/constants/cost-mapper';

type Row = {
    key: string;
    label: string;
    value: number;
    calls: number;
};

const MiniBarRow = ({
    label,
    value,
    calls,
    pct,
}: {
    label: string;
    value: number;
    calls: number;
    pct: number;
}) => (
    <div className='mt-3'>
        <div className='flex items-center justify-between gap-3'>
            <p className='text-sm text-gray-600 dark:text-gray-300 truncate'>{label}</p>
            <p className='text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap'>
                ${formatMoney(value)} · {calls.toLocaleString()} {calls === 1 ? 'call' : 'calls'}
            </p>
        </div>

        <div className='mt-2 h-2 w-full rounded-full bg-gray-200/70 dark:bg-white/10 overflow-hidden'>
            <div
                className='h-full rounded-full bg-black/70 dark:bg-white/70 transition-all'
                style={{ width: `${Math.max(2, pct * 100)}%` }}
            />
        </div>
    </div>
);

const GroupCard = ({
    title,
    icon,
    rows,
    totalValue,
    totalCalls,
}: {
    title: string;
    icon?: React.ReactNode;
    rows: Row[];
    totalValue?: number;
    totalCalls?: number;
}) => {
    const computedValue = rows.reduce((a, r) => a + (Number.isFinite(r.value) ? r.value : 0), 0);
    const computedCalls = rows.reduce((a, r) => a + (Number.isFinite(r.calls) ? r.calls : 0), 0);

    const headerValue = Number.isFinite(totalValue) ? Number(totalValue) : computedValue;
    const headerCalls = Number.isFinite(totalCalls) ? Number(totalCalls) : computedCalls;

    const maxValue = Math.max(...rows.map((r) => r.value), 0);

    return (
        <div className='relative overflow-hidden rounded-2xl border border-gray-200/70 dark:border-borderColor-darkMode bg-gradient-to-b from-white to-gray-50 dark:from-black dark:to-white/5 p-5 shadow-sm hover:shadow-md transition-all'>
            <div className='flex items-start justify-between gap-3'>
                <div className='flex items-center gap-2 min-w-0'>
                    {icon ? (
                        <div className='grid place-items-center h-9 w-9 rounded-xl border border-gray-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5'>
                            {icon}
                        </div>
                    ) : null}
                    <p className='text-sm font-medium text-gray-600 dark:text-gray-300 truncate'>{title}</p>
                </div>

                <span className='shrink-0 inline-flex items-center rounded-full border border-gray-200/70 dark:border-white/10 bg-white/70 dark:bg-white/5 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 backdrop-blur'>
                    <span className='tabular-nums'>{headerCalls.toLocaleString()}</span>
                    <span className='ml-1 text-gray-500 dark:text-gray-400'>
                        {headerCalls === 1 ? 'call' : 'calls'}
                    </span>
                </span>
            </div>

            <div className='mt-4 flex items-baseline gap-2'>
                <span className='text-2xl font-semibold text-black dark:text-white'>$</span>
                <span className='text-3xl font-semibold tracking-tight text-black dark:text-white tabular-nums'>
                    {formatMoney(headerValue)}
                </span>
            </div>

            <div className='mt-4'>
                {rows.map((r) => (
                    <MiniBarRow
                        key={r.key}
                        label={r.label}
                        value={r.value}
                        calls={r.calls}
                        pct={maxValue > 0 ? r.value / maxValue : 0}
                    />
                ))}
            </div>
        </div>
    );
};

const StatsCards = ({
    loading,
    backendResponse,
}: {
    loading?: boolean;
    backendResponse?: any;
}) => {
    const ui = useMemo(() => mapBackendCostsToUi(backendResponse), [backendResponse]);

    if (loading) {
        return (
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
                {Array.from({ length: 3 }).map((_, i) => (
                    <div
                        key={i}
                        className='rounded-2xl border border-gray-200/70 dark:border-borderColor-darkMode bg-white/60 dark:bg-white/5 p-5 animate-pulse'
                    >
                        <div className='h-3 w-32 rounded bg-gray-200 dark:bg-gray-700' />
                        <div className='mt-4 h-8 w-40 rounded bg-gray-200 dark:bg-gray-700' />
                        <div className='mt-6 h-2 w-full rounded bg-gray-200 dark:bg-gray-700' />
                        <div className='mt-3 h-2 w-3/4 rounded bg-gray-200 dark:bg-gray-700' />
                    </div>
                ))}
            </div>
        );
    }

    const byKey = (key: string) =>
        ui.items.find((x: any) => x.key === key) || { totalUsd: 0, calls: 0, key };

    const getStarted = byKey('get_started');
    const scenarioChat = byKey('scenario_chat');
    const chat = byKey('chat');

    const scenarioRuns = byKey('scenario_runs');
    const propertyRuns = byKey('property_runs');
    const runsTotal = byKey('runs_total');

    const chatsRows: Row[] = [
        { key: 'get_started', label: 'Get started', value: Number(getStarted.totalUsd || 0), calls: Number(getStarted.calls || 0) },
        { key: 'scenario_chat', label: 'Scenario chat', value: Number(scenarioChat.totalUsd || 0), calls: Number(scenarioChat.calls || 0) },
        { key: 'chat', label: 'Chat', value: Number(chat.totalUsd || 0), calls: Number(chat.calls || 0) },
    ];

    const runsRows: Row[] = [
        { key: 'scenario_runs', label: 'Scenario runs', value: Number(scenarioRuns.totalUsd || 0), calls: Number(scenarioRuns.calls || 0) },
        { key: 'property_runs', label: 'Property runs', value: Number(propertyRuns.totalUsd || 0), calls: Number(propertyRuns.calls || 0) },
    ];

    const chatsValue = chatsRows.reduce((a, r) => a + r.value, 0);
    const chatsCalls = chatsRows.reduce((a, r) => a + r.calls, 0);

    const totalRows: Row[] = [
        { key: 'chats', label: 'Chats', value: chatsValue, calls: chatsCalls },
        { key: 'runs', label: 'Runs', value: Number(runsTotal.totalUsd || 0), calls: Number(runsTotal.calls || 0) },
    ];

    return (
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
            <GroupCard
                title='Total'
                icon={<span className='text-sm font-semibold'>$</span>}
                rows={totalRows}
                totalValue={Number(ui.totalUsd || 0)}
                totalCalls={Number(ui.calls || 0)}
            />

            <GroupCard
                title='Chats'
                icon={<span className='text-sm'>💬</span>}
                rows={chatsRows}
            />

            <GroupCard
                title='Runs'
                icon={<span className='text-sm'>⚡</span>}
                rows={runsRows}
                totalValue={Number(runsTotal.totalUsd || 0)}
                totalCalls={Number(runsTotal.calls || 0)}
            />
        </div>
    );
};

export default StatsCards;